import { Paper, Progress, SimpleGrid, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useOverview, isNoApiKeys } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'
import type { Overview } from '@/api/whatsapiStats'

function pct(part: number, total: number): number {
  if (total <= 0) return 0
  return Math.min(100, Math.round((part / total) * 100))
}

interface StageProps {
  label: string
  value: number
  color: string
  ratio: number
  loading: boolean
}

function Stage({ label, value, color, ratio, loading }: StageProps) {
  return (
    <Paper shadow="xs" p="md" withBorder>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      {loading ? (
        <Skeleton height={28} width="50%" mt={6} />
      ) : (
        <Text size="xl" fw={700} mt={2}>{value.toLocaleString('pt-BR')}</Text>
      )}
      <Tooltip label={`${ratio}% do enviado`}>
        <Progress value={ratio} color={color} size="sm" mt="xs" aria-label={`${label}: ${ratio}%`} />
      </Tooltip>
    </Paper>
  )
}

interface DeliveryFunnelProps {
  escopo?: EscopoWhatsApi
}

export function DeliveryFunnel({ escopo }: DeliveryFunnelProps = {}) {
  const { period } = useWhatsApiPeriod()
  const query = useOverview(period, { escopo })
  const raw = query.data
  const ov: Overview | undefined = raw && !isNoApiKeys(raw) ? raw : undefined
  const loading = query.isLoading

  const sent = ov?.sent ?? 0
  const delivered = ov?.delivered ?? 0
  const read = ov?.read ?? 0
  const noReceipts = !loading && sent > 0 && delivered === 0 && read === 0

  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>Funil de entrega</Text>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <Stage label="Aceitas" value={sent} color="green" ratio={100} loading={loading} />
        <Stage label="Entregues" value={delivered} color="teal" ratio={pct(delivered, sent)} loading={loading} />
        <Stage label="Lidas" value={read} color="lime" ratio={pct(read, sent)} loading={loading} />
      </SimpleGrid>
      {noReceipts && (
        <Text size="xs" c="dimmed" data-testid="funnel-no-receipts">
          Entregue/Lido aparecerão quando a engine de receipts estiver ativa.
        </Text>
      )}
    </Stack>
  )
}
